import type { AnyPgTable } from "drizzle-orm/pg-core";

import type * as schema from "@/db/schema";
import { createLogger } from "@/logger";

const log = createLogger("events");

type TableListener = (tableName: TableName) => void;

// Attempt to derive the literal table name string from the table definition when available.
// Falls back to the table key type, which still provides compile-time safety for known tables.
export type TableName = {
  [K in keyof typeof schema]: (typeof schema)[K] extends AnyPgTable
    ? (typeof schema)[K] extends { _?: { name?: infer N } }
      ? N extends string
        ? N
        : never
      : never
    : never;
}[keyof typeof schema] &
  string;

class TableEventManagerImpl {
  private domNode: Element;
  private batchDepth = 0;
  private pendingTables = new Set<TableName>();

  public constructor() {
    this.domNode = document.createElement("div");
  }

  public async batch<T>(fn: () => Promise<T>): Promise<T> {
    this.batchDepth++;
    try {
      return await fn();
    } finally {
      this.batchDepth--;
      if (this.batchDepth === 0 && this.pendingTables.size > 0) {
        const tables = [...this.pendingTables];
        this.pendingTables.clear();
        for (const table of tables) {
          logInBrightRed(table);
          this.domNode.dispatchEvent(new Event(table));
        }
      }
    }
  }

  public tableUpdated(tableName: TableName) {
    if (this.batchDepth > 0) {
      this.pendingTables.add(tableName);
      return;
    }
    logInBrightRed(tableName);
    this.domNode.dispatchEvent(new Event(tableName));
  }

  public subscribe(tables: readonly TableName[], listener: TableListener): () => void {
    const listeners: Record<string, EventListener> = {};

    for (const table of tables) {
      listeners[table] = () => listener(table);
      this.domNode.addEventListener(table, listeners[table]);
    }

    return () => {
      for (const table of tables) {
        this.domNode.removeEventListener(table, listeners[table]);
      }
    };
  }
}

export const TableEventManager = new TableEventManagerImpl();

export function emitTableUpdates(tables: readonly TableName[]): void {
  for (const table of tables) {
    TableEventManager.tableUpdated(table);
  }
}

function logInBrightRed(tableName: TableName): void {
  log.debug("[table_updated]", tableName);
}
